import {
  Typography,
  Paper,
  Stack,
  Button,
  IconButton,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useBoolean } from 'src/hooks/use-boolean';
import PropTypes from 'prop-types';
import Iconify from 'src/components/iconify';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import FormProvider from 'src/components/hook-form/form-provider';
import { RHFTextField } from 'src/components/hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFieldArray, useForm } from 'react-hook-form';
import { putOrgDetails } from 'src/services/user.service';
import { useSnackbar } from 'notistack';
import UploadProfile from './upload-profile';
import { uploadImageToS3 } from 'src/services/aws.service';
import { RouterLink } from 'src/routes/components';
import { getFileExtension } from 'src/utils/get-file-extension';

const ICONS_MAP = {
  linkedIn: { enabled: 'devicon:linkedin', disabled: 'f7:logo-linkedin' },
  twitter: { enabled: 'devicon:twitter', disabled: 'ri:twitter-x-line' },
  facebook: { enabled: 'logos:facebook', disabled: 'ic:baseline-facebook' },
  instagram: {
    enabled: 'skill-icons:instagram',
    disabled: 'uil:instagram-alt',
  },
  youtube: { enabled: 'logos:youtube-icon', disabled: 'simple-icons:youtube' },
};

export default function OrganizationProfile({
  orgId,
  orgProfileImageUrl,
  orgName,
  about,
  websiteUrl,
  socialLinks,
  refetch,
}) {
  // states
  const [imageFile, setImageFile] = useState(null);
  const isEditMode = useBoolean(false);
  const isProfileComplete = useBoolean(false);
  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = useMemo(
    () => ({
      orgName,
      orgProfileImageUrl,
      about,
      websiteUrl,
      socialLinks,
    }),
    [about, orgName, orgProfileImageUrl, socialLinks, websiteUrl],
  );

  const schema = Yup.object().shape({
    orgName: Yup.string().required('Name is required'),
    orgProfileImageUrl: Yup.string(),
    about: Yup.string(),
    websiteUrl: Yup.string(),
    socialLinks: Yup.array().of(
      Yup.object().shape({
        name: Yup.string(),
        url: Yup.string(),
      }),
    ),
  });

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const {
    watch,
    setValue,
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = methods;

  const { fields: socialLinksFields } = useFieldArray({
    name: 'socialLinks',
    control,
  });

  const getImageUrl = async () => {
    try {
      const url = await uploadImageToS3(
        imageFile,
        'org-profiles',
        imageFile.type,
        getFileExtension(imageFile.name),
      );

      return url;
    } catch (error) {
      console.log(error);
      throw new Error('Failed to upload image');
    }
  };

  const onSubmit = handleSubmit(async data => {
    try {
      const socialLinksMap = data.socialLinks.reduce((acc, link) => {
        const socialMediaName =
          link.name.charAt(0).toUpperCase() + link.name.slice(1);
        acc[socialMediaName] = link.url;
        return acc;
      }, {});

      const payload = {
        name: data.orgName,
        about: data.about,
        social_link_map: socialLinksMap,
        website: data.websiteUrl,
      };

      if (imageFile) {
        const image = await getImageUrl();
        payload.org_logo_url = image;
      }

      const response = await putOrgDetails(payload, orgId);

      if (response.success) {
        refetch();
        isEditMode.onFalse();
        enqueueSnackbar('Organization details updated successfully');
      } else throw new Error(response.message);
    } catch (error) {
      console.log(error);
      enqueueSnackbar('Something went wrong', { variant: 'error' });
    } finally {
      setImageFile(null);
      isEditMode.onFalse();
    }
  });

  // effects
  useEffect(() => {
    setValue('orgName', orgName);
    setValue('about', about);
    setValue('websiteUrl', websiteUrl);

    socialLinks.forEach((socialLink, index) => {
      setValue(`socialLinks.${index}.name`, socialLink.name || '');
      setValue(`socialLinks.${index}.url`, socialLink.url || '');
    });
  }, [orgName, about, websiteUrl, socialLinks, setValue]);

  useEffect(() => {
    const checkProfileCompletion = () => {
      if (
        orgName &&
        about &&
        websiteUrl &&
        socialLinks.every(socialLink => socialLink.url !== '')
      ) {
        isProfileComplete.onTrue();
      } else {
        isProfileComplete.onFalse();
      }
    };

    checkProfileCompletion();
  }, [about, isProfileComplete, orgName, socialLinks, watch, websiteUrl]);

  return (
    <Paper
      variant="outlined"
      sx={{
        px: '20px',
        py: '24px',
      }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Stack spacing="20px">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="subheading2">Profile</Typography>
            {isEditMode.value ? (
              <Stack direction="row" spacing="8px">
                <Button
                  size="small"
                  color="secondary"
                  variant="contained"
                  onClick={() => {
                    setImageFile(null);
                    isEditMode.onFalse();
                  }}
                >
                  Cancel
                </Button>
                <LoadingButton
                  loading={isSubmitting}
                  size="small"
                  variant="contained"
                  type="submit"
                >
                  Save
                </LoadingButton>
              </Stack>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Iconify icon="tabler:pencil" />}
                onClick={isEditMode.onTrue}
              >
                Edit
              </Button>
            )}
          </Stack>
          <UploadProfile
            orgProfileImageUrl={orgProfileImageUrl}
            isEditMode={isEditMode}
            imageFile={imageFile}
            setImageFile={setImageFile}
          />
          <Stack spacing="8px">
            <Typography variant="subheading2">Organization name</Typography>
            {isEditMode.value ? (
              <RHFTextField name="orgName" placeholder="e.g. Hirentel AI" />
            ) : (
              <Typography variant="input">{orgName}</Typography>
            )}
          </Stack>
          <Stack spacing="8px">
            <Typography variant="subheading2">About</Typography>
            {isEditMode.value ? (
              <RHFTextField
                multiline
                minRows={5}
                name="about"
                placeholder="e.g. Founded with a vision to innovate recruitment, our company specializes in creating AI-driven solutions tailored for HR professionals and organizations. With our flagship product, Interview AI, we're transforming the way companies hire by automating interviews, ensuring unbiased evaluations, and streamlining the talent acquisition process. Committed to excellence and forward-thinking, we provide smart, efficient, and customizable tools to align with today's dynamic business environment and help our clients make smarter hiring decisions."
              />
            ) : (
              <Typography variant="input">{about || '-'}</Typography>
            )}
          </Stack>
          <Stack spacing="8px">
            <Typography variant="subheading2">Website</Typography>
            {isEditMode.value ? (
              <RHFTextField
                name="websiteUrl"
                placeholder="e.g. www.hireintel.app"
              />
            ) : (
              <Typography variant="input">{websiteUrl || '-'}</Typography>
            )}
          </Stack>
          <Stack spacing="8px">
            <Typography variant="subheading2">Social</Typography>
            {isEditMode.value ? (
              <Stack spacing="12px">
                {socialLinksFields.map((field, index) => {
                  const parentField = `socialLinks.${index}`;

                  const nameField = `${parentField}.name`;
                  const name = watch(nameField);
                  const urlField = `${parentField}.url`;

                  return (
                    <Stack
                      key={field.id}
                      direction="row"
                      alignItems="center"
                      spacing="12px"
                    >
                      <Iconify width={24} icon={ICONS_MAP[name].enabled} />
                      <RHFTextField
                        name={urlField}
                        placeholder={`e.g. www.${name}.com/hireintel`}
                      />
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <Stack direction="row" alignItems="center" spacing="8px">
                {socialLinks.map(socialLink => (
                  <IconButton
                    component={RouterLink}
                    target="_blank"
                    href={socialLink.url || '#'}
                    disabled={!socialLink.url}
                    key={socialLink.name}
                  >
                    <Iconify
                      color={theme =>
                        socialLink.url
                          ? 'inherit'
                          : theme.palette.background.primaryGrey3
                      }
                      width={24}
                      icon={
                        ICONS_MAP[socialLink.name][
                          socialLink.url ? 'enabled' : 'disabled'
                        ]
                      }
                    />
                  </IconButton>
                ))}
              </Stack>
            )}
          </Stack>
          {!isProfileComplete.value && !isEditMode.value && (
            <Stack
              bgcolor="background.primaryGrey2"
              direction="row"
              spacing="8px"
              alignItems="center"
              padding="8.5px 8px"
              borderRadius="6px"
            >
              <Iconify icon="tabler:alert" width={20} />
              <Stack direction="row" spacing="4px" alignItems="center">
                <Typography variant="input" color="content.primaryBlack2">
                  Your profile is incomplete.
                </Typography>
                <Typography
                  variant="input"
                  color="content.primaryBlack2"
                  fontWeight={600}
                  onClick={isEditMode.onTrue}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Complete profile
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
      </FormProvider>
    </Paper>
  );
}

OrganizationProfile.propTypes = {
  orgId: PropTypes.string,
  orgName: PropTypes.string,
  about: PropTypes.string,
  websiteUrl: PropTypes.string,
  socialLinks: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
  refetch: PropTypes.func,
  orgProfileImageUrl: PropTypes.string,
};
